/**
 * Compliance tab — S500 checklist driven by inspect_connect's
 * pre-computed status (compute_s500_status). One row per rule with a
 * traffic-light dot, the §-reference, expandable standard text, and a
 * "missing rooms" badge when applicable.
 *
 * Also surfaces evidence the rules touch: containments, antimicrobials,
 * dry standards, authorizations — pulled from `manifest.s500` so the
 * office user can see the specific records the engine is evaluating.
 *
 * S500 PDF packet is one click away — IC already generates it; we just
 * link to the proxy endpoint.
 */
import { api } from "../../api/client.ts";
import type {
  IcProject,
  Manifest,
  ManifestS500Block,
  S500ComplianceStatus,
  S500ComplianceRule,
  S500RuleStatus,
} from "../../types.ts";

const STATUS_TONE: Record<S500RuleStatus, { color: string; label: string }> = {
  ok: { color: "#4aff6a", label: "OK" },
  warning: { color: "#ffaa4a", label: "WARNING" },
  non_compliant: { color: "#ff4a4a", label: "NON-COMPLIANT" },
  n_a: { color: "#666", label: "N/A" },
};

const OVERALL_TONE: Record<
  S500ComplianceStatus["overall"],
  { color: string; bg: string; label: string }
> = {
  compliant: { color: "#4aff6a", bg: "rgba(74,255,106,.12)", label: "Compliant" },
  warning: { color: "#ffaa4a", bg: "rgba(255,170,74,.14)", label: "Warnings" },
  non_compliant: { color: "#ff4a4a", bg: "rgba(255,74,74,.14)", label: "Non-compliant" },
};

// Friendly rule names — fall back to the raw rule id if missing.
const RULE_LABELS: Record<string, string> = {
  loss_date_set: "Loss date documented",
  severity_per_room: "Per-room severity (Cat × Class)",
  dry_standard_day0: "Day-0 dry standard captured",
  containment_cat2: "Containment for Cat 2/3",
  antimicrobial_cat2: "Antimicrobial for Cat 2/3",
  authorization_initial: "Initial authorization signed",
  authorization_change_order: "Change-order signed when scope mutates",
  monitoring_daily: "Daily monitoring readings",
  equipment_plan_present: "Equipment plan (calculated)",
};

export interface ComplianceTabOptions {
  container: HTMLElement;
  project: IcProject;
  manifest: Manifest | null;
}

export class ComplianceTab {
  private status: S500ComplianceStatus | null = null;
  private statusError: string | null = null;
  private opts: ComplianceTabOptions;

  constructor(opts: ComplianceTabOptions) {
    this.opts = opts;
    this.renderLoading();
    this.fetch();
  }

  private renderLoading(): void {
    this.opts.container.innerHTML = "";
    this.opts.container.style.cssText =
      "padding:32px;color:var(--text-muted);text-align:center;font-size:13px;";
    this.opts.container.textContent = "Loading S500 compliance…";
  }

  private async fetch(): Promise<void> {
    try {
      this.status = await api.ic.getS500Status(this.opts.project.project_id);
    } catch (err) {
      this.statusError = (err as Error).message || "Failed to load";
    }
    this.render();
  }

  private render(): void {
    const root = this.opts.container;
    root.innerHTML = "";
    root.style.cssText = "padding:16px;display:flex;flex-direction:column;gap:16px;";

    if (this.statusError) {
      const err = document.createElement("div");
      err.style.cssText =
        "padding:14px 16px;background:var(--surface);border:1px solid var(--red);border-radius:var(--radius);color:var(--red);font-size:13px;";
      err.textContent = `Compliance status unavailable: ${this.statusError}`;
      root.appendChild(err);
      return;
    }

    if (this.status) root.appendChild(this.overallBlock(this.status));
    root.appendChild(this.actionsBlock());
    if (this.status) root.appendChild(this.rulesBlock(this.status.rules));
    root.appendChild(this.evidenceBlock());
  }

  private overallBlock(s: S500ComplianceStatus): HTMLElement {
    const tone = OVERALL_TONE[s.overall];
    const wrap = document.createElement("section");
    wrap.style.cssText = `
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: var(--radius);
      padding: 14px 16px;
      display: flex;
      align-items: center;
      gap: 14px;
    `;
    const dot = document.createElement("div");
    dot.style.cssText = `width:36px;height:36px;border-radius:50%;background:${tone.bg};display:flex;align-items:center;justify-content:center;color:${tone.color};font-size:18px;font-weight:700;`;
    dot.textContent = s.overall === "compliant" ? "✓" : s.overall === "warning" ? "!" : "✕";
    const meta = document.createElement("div");
    meta.style.cssText = "display:flex;flex-direction:column;gap:2px;";
    const label = document.createElement("div");
    label.textContent = tone.label;
    label.style.cssText = `font-size:14px;font-weight:600;color:${tone.color};`;
    const sub = document.createElement("div");
    const counts = countByStatus(s.rules);
    const parts: string[] = [];
    if (counts.ok) parts.push(`${counts.ok} ok`);
    if (counts.warning) parts.push(`${counts.warning} warning`);
    if (counts.non_compliant) parts.push(`${counts.non_compliant} non-compliant`);
    if (counts.n_a) parts.push(`${counts.n_a} n/a`);
    sub.textContent = parts.join(" · ") || "no rules evaluated";
    sub.style.cssText = "font-size:12px;color:var(--text-muted);";
    meta.appendChild(label);
    meta.appendChild(sub);
    wrap.appendChild(dot);
    wrap.appendChild(meta);
    return wrap;
  }

  private actionsBlock(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.style.cssText = "display:flex;gap:8px;flex-wrap:wrap;";
    const link = document.createElement("a");
    link.href = api.ic.s500PacketUrl(this.opts.project.project_id);
    link.target = "_blank";
    link.rel = "noopener";
    link.className = "btn primary";
    link.style.cssText =
      "display:inline-flex;align-items:center;gap:6px;text-decoration:none;";
    link.textContent = "↓ Download S500 packet (PDF)";
    wrap.appendChild(link);
    return wrap;
  }

  private rulesBlock(rules: S500ComplianceRule[]): HTMLElement {
    const wrap = section("Rules");
    if (rules.length === 0) {
      wrap.appendChild(emptyText("No rules evaluated."));
      return wrap;
    }
    const list = document.createElement("div");
    list.style.cssText = "display:flex;flex-direction:column;gap:6px;";
    for (const r of rules) list.appendChild(renderRule(r));
    wrap.appendChild(list);
    return wrap;
  }

  private evidenceBlock(): HTMLElement {
    const wrap = section("Evidence");
    const s500: ManifestS500Block | null = this.opts.manifest?.s500 ?? null;

    if (!s500) {
      wrap.appendChild(emptyText("No S500 records — they appear here once the inspector logs them."));
      return wrap;
    }

    const counts = [
      ["Containments", s500.containments.length],
      ["Antimicrobials", s500.antimicrobials.length],
      ["Dry standards", s500.dry_standards.length],
      ["Authorizations", s500.authorizations.length],
      ["Equipment plan", s500.equipment_plan ? 1 : 0],
    ] as const;

    const grid = document.createElement("div");
    grid.style.cssText =
      "display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:8px;";
    for (const [label, n] of counts) grid.appendChild(evidenceCell(label, n));
    wrap.appendChild(grid);

    // Detail lists for each non-empty section.
    if (s500.authorizations.length > 0) {
      wrap.appendChild(detailHeader("Authorizations"));
      for (const a of s500.authorizations) {
        const line = document.createElement("div");
        line.style.cssText = "font-size:12px;color:var(--text);line-height:1.5;";
        line.innerHTML = `
          <strong>${escapeHtml(a.kind)}</strong>
          · ${escapeHtml(a.signer_name)} (${escapeHtml(a.signer_role)})
          · ${formatDate(a.signed_at)}
        `;
        wrap.appendChild(line);
      }
    }

    if (s500.antimicrobials.length > 0) {
      wrap.appendChild(detailHeader("Antimicrobial applications"));
      for (const am of s500.antimicrobials) {
        const line = document.createElement("div");
        line.style.cssText = "font-size:12px;color:var(--text);line-height:1.5;";
        line.innerHTML = `
          <strong>${escapeHtml(am.product_name)}</strong>
          · EPA ${escapeHtml(am.epa_reg_number)}
          · ${formatNumber(am.area_sqft)} sqft
          · by ${escapeHtml(am.applied_by)}
          · ${formatDate(am.applied_at)}
        `;
        wrap.appendChild(line);
      }
    }

    if (s500.containments.length > 0) {
      wrap.appendChild(detailHeader("Containments"));
      for (const c of s500.containments) {
        const line = document.createElement("div");
        line.style.cssText = "font-size:12px;color:var(--text);line-height:1.5;";
        const removed = c.removed_at ? ` → removed ${formatDate(c.removed_at)}` : "";
        line.textContent = `${c.type} · room ${shortId(c.room_id)} · erected ${formatDate(c.erected_at)}${removed}`;
        wrap.appendChild(line);
      }
    }

    return wrap;
  }
}

// ── helpers ─────────────────────────────────────────────────────────────────

function countByStatus(rules: S500ComplianceRule[]): Record<S500RuleStatus, number> {
  const out: Record<S500RuleStatus, number> = { ok: 0, warning: 0, non_compliant: 0, n_a: 0 };
  for (const r of rules) out[r.status] = (out[r.status] || 0) + 1;
  return out;
}

function renderRule(r: S500ComplianceRule): HTMLElement {
  const tone = STATUS_TONE[r.status];
  const row = document.createElement("details");
  row.style.cssText = `
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 0;
    overflow: hidden;
  `;

  const summary = document.createElement("summary");
  summary.style.cssText = `
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 10px;
    align-items: center;
    padding: 10px 14px;
    cursor: pointer;
    list-style: none;
  `;
  // Status pill
  const pill = document.createElement("span");
  pill.textContent = tone.label;
  pill.style.cssText = `
    display:inline-block;padding:1px 8px;border-radius:99px;
    font-size:10px;font-weight:700;letter-spacing:.4px;
    background:${tone.color === "#666" ? "var(--border)" : tone.color + "22"};
    color:${tone.color};
    flex-shrink:0;white-space:nowrap;
  `;
  // Title + section ref
  const title = document.createElement("div");
  title.style.cssText = "min-width:0;";
  const t1 = document.createElement("div");
  t1.textContent = RULE_LABELS[r.rule] || r.rule;
  t1.style.cssText = "font-size:13px;color:var(--text);font-weight:600;";
  title.appendChild(t1);
  if (r.section_ref) {
    const t2 = document.createElement("div");
    t2.textContent = r.section_ref;
    t2.style.cssText = "font-size:11px;color:var(--text-muted);font-family:monospace;";
    title.appendChild(t2);
  }
  // Right side: missing-rooms count, if any
  const right = document.createElement("div");
  right.style.cssText = "font-size:11px;color:var(--text-muted);";
  if (r.missing_rooms.length > 0) {
    right.textContent = `${r.missing_rooms.length} room${r.missing_rooms.length === 1 ? "" : "s"} missing`;
  }
  summary.appendChild(pill);
  summary.appendChild(title);
  summary.appendChild(right);
  row.appendChild(summary);

  // Body — standard text, detail, missing rooms
  const body = document.createElement("div");
  body.style.cssText = `
    padding: 0 14px 12px;
    font-size: 12px;
    color: var(--text-muted);
    line-height: 1.55;
    border-top: 1px solid var(--border);
    margin-top: 0;
  `;
  if (r.standard_text) {
    const p = document.createElement("p");
    p.textContent = r.standard_text;
    p.style.cssText = "margin-top:10px;";
    body.appendChild(p);
  }
  if (r.detail) {
    const p = document.createElement("p");
    p.textContent = r.detail;
    p.style.cssText = "margin-top:8px;color:var(--text);";
    body.appendChild(p);
  }
  if (r.missing_rooms.length > 0) {
    const w = document.createElement("div");
    w.style.cssText = "margin-top:8px;display:flex;flex-wrap:wrap;gap:4px;";
    for (const id of r.missing_rooms) {
      const chip = document.createElement("span");
      chip.textContent = shortId(id);
      chip.style.cssText =
        "font-size:11px;background:var(--border);padding:1px 8px;border-radius:99px;color:var(--text);font-family:monospace;";
      w.appendChild(chip);
    }
    body.appendChild(w);
  }
  if (!body.firstChild) {
    const empty = document.createElement("div");
    empty.style.cssText = "margin-top:10px;font-style:italic;";
    empty.textContent = "No additional detail.";
    body.appendChild(empty);
  }
  row.appendChild(body);

  return row;
}

function section(title: string): HTMLElement {
  const card = document.createElement("section");
  card.style.cssText = `
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  `;
  const h = document.createElement("h3");
  h.textContent = title;
  h.style.cssText =
    "font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.4px;color:var(--text-muted);margin:0;";
  card.appendChild(h);
  return card;
}

function detailHeader(text: string): HTMLElement {
  const h = document.createElement("div");
  h.textContent = text;
  h.style.cssText =
    "font-size:11px;color:var(--text-muted);font-weight:600;text-transform:uppercase;letter-spacing:.3px;margin-top:6px;";
  return h;
}

function emptyText(text: string): HTMLElement {
  const w = document.createElement("div");
  w.textContent = text;
  w.style.cssText = "color:var(--text-muted);font-size:12px;";
  return w;
}

function evidenceCell(label: string, count: number): HTMLElement {
  const w = document.createElement("div");
  w.style.cssText = `
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 8px 10px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  `;
  const v = document.createElement("div");
  v.textContent = String(count);
  v.style.cssText = `font-size:18px;font-weight:700;color:${count > 0 ? "var(--text)" : "var(--text-muted)"};font-variant-numeric:tabular-nums;`;
  const l = document.createElement("div");
  l.textContent = label;
  l.style.cssText = "font-size:10px;text-transform:uppercase;letter-spacing:.3px;color:var(--text-muted);";
  w.appendChild(v);
  w.appendChild(l);
  return w;
}

function shortId(id: string): string {
  return id.length > 12 ? id.slice(0, 8) + "…" : id;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

function formatNumber(n: number): string {
  return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
